import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Localization from "aldoo-ra/Localization"
import { store } from "aldoo-ra/Store"
import PlanCard from "./PlanCard"

const MONTHLY_PID = "snap-pro-monthly"
const ANNUAL_PID = "snap-pro-annual"

function resolveProduct(storeData, pid) {
  const product = Object.values(storeData.products || {}).find((p) => p.pid === pid)
  if (!product) return null
  const priceId = product.price?._id || product.price
  const storePrice = storeData.prices?.[String(priceId)]
  const model = storePrice?.price_model?.find((m) => m.primary) || storePrice?.price_model?.[0]
  if (!model) return null
  return { product, storePrice, model }
}

/**
 * Per-seat Pro subscription card. The billing toggle picks between the
 * monthly and annual store products ($/user); checkout goes through the
 * regular store cart with the selected product.
 */
export default function ProCard({ storeData, billing, activePid, featuresByPid = {} }) {
  const texts = TextBundle({ id: "pricing" })
  const { template } = Localization()
  const monthly = resolveProduct(storeData, MONTHLY_PID)
  const annual = resolveProduct(storeData, ANNUAL_PID)
  const selected = billing === "annual" ? (annual || monthly) : (monthly || annual)

  const monthlyAmount = monthly?.model?.amount ?? null
  const annualAmount = annual?.model?.amount ?? null
  const annualPerMonth = annualAmount != null ? Math.round((annualAmount / 12) * 100) / 100 : null
  const displayAmount = billing === "annual" && annualPerMonth != null ? annualPerMonth : monthlyAmount

  const handleBuy = () => {
    if (!selected) return
    const { product, storePrice, model } = selected
    const webEntry = model.real_product_id?.find((r) => {
      const platform = typeof r.platform === "object" ? r.platform?.value : r.platform
      return platform === "Web"
    })
    store.addToCart({
      product: {
        pid: product.pid,
        product_id: storePrice._id,
        store_id: webEntry?.id || null,
        name: product.name,
        description: product.description,
        currency: model.currency || "USD",
        price_model: [{ ...model, primary: true }],
        duration: billing === "annual" ? "per year" : "per month",
        thumbnail: product.thumbnail,
      },
      proceedToCart: true,
    })
  }

  const highlights = featuresByPid[selected?.product?.pid] || []

  const isCurrent = activePid && selected?.product?.pid === activePid

  return (
    <PlanCard
      className="grad_border_6 shadow-md bg-paper"
      badge={texts?.pro_badge}
      title={texts?.pro_title}
      subtitle={texts?.pro_subtitle}
      price={
        displayAmount != null ? (
          <>
            <Typography component="span" variant="h2">
              ${displayAmount}
            </Typography>
            <span className="text-text_secondary text-sm ml-1">
              {texts?.pro_price_unit}
            </span>

            {billing === "annual" && annualAmount != null && (
              <Typography variant="txt2_dim" className="block mt-1">
                {template(texts?.pro_billed_annually_note || "billed annually · ${{amount}} per user/year", {
                  amount: annualAmount
                })}
              </Typography>
            )}

            {billing === "monthly" && annualPerMonth != null && (
              <Typography variant="txt2_dim" className="block mt-1">
                {template(texts?.pro_annual_equivalent_note || "${{amount}} per user/mo if billed annually", {
                  amount: annualPerMonth
                })}
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="txt2_dim">
            {texts?.pricing_unavailable}
          </Typography>
        )
      }
      button={
        isCurrent ? (
          <Button
            variant="primary"
            className="w-full mb-6 disabled:opacity-100 cursor-default bg-success hover:bg-success"
            disabled
          >
            {texts?.current_plan_button}
          </Button>
        ) : (
          <Button
            variant="primary"
            className="w-full mb-6"
            disabled={!selected}
            onClick={handleBuy}
          >
            {texts?.continue_button}
          </Button>
        )
      }
      highlights={highlights}
    />
  )
}
